import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";

function money(value, currency = "EUR") {
  const symbol = currency === "USD" ? "$" : "€";
  return `${symbol}${Number(value || 0).toFixed(2)}`;
}

function slugForRecipe(item = {}) {
  return String(item.slug || item.recipe_slug || item.title || item.recipe_name || "smart-kitchen-suggestion")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "smart-kitchen-suggestion";
}

function expiryLabel(item, t) {
  const state = item?.expiry_state || (item?.days_until_expiry < 0 ? "expired" : item?.days_until_expiry === 0 ? "expires_today" : "expiring");
  if (state === "expired") return t("smart_kitchen.expiring.expired");
  if (state === "expires_today") return t("smart_kitchen.expiring.today");
  return t("smart_kitchen.expiring.in_days", { days: item?.days_until_expiry || 0 });
}

function suggestionDetailText(item, t) {
  const details = [];
  const expiringMatch = (item?.matched_items || []).find((match) => match?.is_expiring) || (item?.matched_inventory || [])[0];

  if (expiringMatch?.name) {
    const days = Number(expiringMatch.days_until_expiry ?? 0);
    if (days < 0) {
      details.push(t("smart_kitchen.suggestions.uses_expired_item", { item: expiringMatch.name }));
    } else if (days === 0) {
      details.push(t("smart_kitchen.suggestions.uses_expiring_today", { item: expiringMatch.name }));
    } else {
      details.push(t("smart_kitchen.suggestions.uses_expiring_item", { item: expiringMatch.name, days }));
    }
  }

  if (Number(item?.matched_pantry_count || 0) > 0) {
    details.push(t("smart_kitchen.suggestions.uses_pantry_items", { count: item.matched_pantry_count }));
  }

  if (Number.isFinite(Number(item?.missing_ingredient_count))) {
    details.push(t("smart_kitchen.suggestions.missing_ingredients", { count: item.missing_ingredient_count }));
  }

  return details.length ? details.join(" · ") : item?.body || t("smart_kitchen.suggestions.default_body");
}

function savingsExplanationText(item, currency, t) {
  const store = item?.store_name ? t("smart_kitchen.savings.explanation_store", { store: item.store_name }) : "";
  const provider = item?.store_provider || item?.provider || item?.store_chain;
  const providerText = provider ? t("smart_kitchen.savings.explanation_provider", { provider }) : "";
  const savings = money(item?.savings_amount, currency);
  const pricePair = t("smart_kitchen.savings.explanation_prices", {
    chosen: money(item?.chosen_price, currency),
    comparison: money(item?.comparison_price ?? item?.current_price, currency),
  });
  return [store, providerText, t("smart_kitchen.savings.explanation_savings", { value: savings }), pricePair].filter(Boolean).join(" · ");
}

function scoreItemLabel(key, t) {
  return t(`smart_kitchen.score.items.${key}`, { defaultValue: key });
}

function recommendationLabel(recommendation, t) {
  if (!recommendation?.key) return "";
  return t(`smart_kitchen.score.recommendations.${recommendation.key}`, {
    points: recommendation.points || 0,
    defaultValue: recommendation.key,
  });
}

function pantryHealthGradeLabel(grade, t) {
  return t(`smart_kitchen.pantry_health.grade.${grade || "high_risk"}`, {
    defaultValue: grade || "high_risk",
  });
}

function pantryHealthReasonLabel(reason, t) {
  if (!reason?.key) return "";
  if (Number(reason.count) === 1) {
    return t(`smart_kitchen.pantry_health.reasons.${reason.key}_one`, {
      count: reason.count,
      defaultValue: t(`smart_kitchen.pantry_health.reasons.${reason.key}`, {
        count: reason.count || 0,
        defaultValue: reason.key,
      }),
    });
  }
  return t(`smart_kitchen.pantry_health.reasons.${reason.key}`, {
    count: reason.count || 0,
    defaultValue: reason.key,
  });
}

function pantryHealthRecommendationLabel(recommendation, t) {
  if (!recommendation?.key) return "";
  return t(`smart_kitchen.pantry_health.recommendations.${recommendation.key}`, {
    defaultValue: recommendation.key,
  });
}

function foodWasteRiskReasonLabel(reason, t) {
  if (!reason?.key) return "";
  if (Number(reason.count) === 1) {
    return t(`smart_kitchen.food_waste_risk.reasons.${reason.key}_one`, {
      count: reason.count,
      defaultValue: t(`smart_kitchen.food_waste_risk.reasons.${reason.key}`, {
        count: reason.count || 0,
        defaultValue: reason.key,
      }),
    });
  }
  return t(`smart_kitchen.food_waste_risk.reasons.${reason.key}`, {
    count: reason.count || 0,
    defaultValue: reason.key,
  });
}

function foodWasteRiskRecommendationLabel(recommendation, t) {
  if (!recommendation?.key) return "";
  return t(`smart_kitchen.food_waste_risk.recommendations.${recommendation.key}`, {
    defaultValue: recommendation.key,
  });
}

function SectionCard({ icon, title, subtitle, state, children, emptyContent, lockedContent, onClick, testId }) {
  const { t } = useTranslation();
  const isEmpty = state === "empty";
  const isLocked = state === "locked";
  const canNavigate = Boolean(onClick) && !isLocked;
  const stateLabelKey = `smart_kitchen.state.${state || "empty"}`;
  const chipColor = isLocked ? "default" : isEmpty ? "warning" : "success";
  const content = (
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Box
            sx={{
              alignItems: "center",
              bgcolor: "action.hover",
              borderRadius: 1,
              display: "inline-flex",
              flex: "0 0 auto",
              height: 40,
              justifyContent: "center",
              width: 40,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            color={chipColor}
            variant={isLocked ? "outlined" : "filled"}
            icon={isLocked ? <LockRoundedIcon /> : null}
            label={t(stateLabelKey)}
            sx={{ flex: "0 0 auto" }}
          />
          {canNavigate ? <ArrowForwardRoundedIcon color="primary" aria-label={t("smart_kitchen.open_section")} /> : null}
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {isLocked ? (
          lockedContent || (
            <Typography variant="body2" color="text.secondary">
              {t("smart_kitchen.locked_body")}
            </Typography>
          )
        ) : isEmpty ? (
          emptyContent || (
          <Typography variant="body2" color="text.secondary">
            {t("smart_kitchen.empty_section")}
          </Typography>
          )
        ) : (
          children
        )}
      </CardContent>
  );
  return (
    <Card sx={{ height: "100%", borderRadius: 1, boxShadow: 2 }}>
      {canNavigate ? (
        <CardActionArea
          onClick={onClick}
          data-testid={testId}
          aria-label={`${title}. ${t("smart_kitchen.open_section")}`}
          sx={{ height: "100%", alignItems: "stretch" }}
        >
          {content}
        </CardActionArea>
      ) : content}
    </Card>
  );
}

function GuidedEmptyState({ title, body, examples = [], primaryAction, secondaryAction }) {
  return (
    <Stack spacing={1.25} alignItems="flex-start">
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {body}
        </Typography>
      </Box>
      {examples.length ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {examples.map((example) => (
            <Chip key={example} size="small" variant="outlined" label={example} />
          ))}
        </Stack>
      ) : null}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
        {primaryAction ? (
          <Button
            size="small"
            variant="contained"
            onClick={primaryAction.onClick}
            data-testid={primaryAction.testId}
          >
            {primaryAction.label}
          </Button>
        ) : null}
        {secondaryAction ? (
          <Button
            size="small"
            variant="outlined"
            onClick={secondaryAction.onClick}
            data-testid={secondaryAction.testId}
          >
            {secondaryAction.label}
          </Button>
        ) : null}
      </Stack>
    </Stack>
  );
}

function CompactList({ items, primaryText, secondaryText, emptyLabel }) {
  if (!items?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <List dense disablePadding>
      {items.map((item, index) => (
        <ListItem key={item.id || item.recipe_id || item.item_id || index} disableGutters>
          <ListItemText
            primary={primaryText(item)}
            secondary={secondaryText ? secondaryText(item) : null}
            primaryTypographyProps={{ fontWeight: 700 }}
          />
        </ListItem>
      ))}
    </List>
  );
}

export default function SmartKitchenDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { country = "en" } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const errorFallback = t("smart_kitchen.error_body");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.smartKitchen
      .getDashboard({ lang: i18n.language || "en" })
      .then((res) => {
        if (!active) return;
        setDashboard(res?.data?.data || null);
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || err?.message || errorFallback);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [i18n.language, errorFallback]);

  const data = useMemo(() => dashboard || {}, [dashboard]);
  const pantryHealth = data.pantry_health || {};
  const foodWasteRisk = data.food_waste_risk || {};
  const cookFromPantry = data.cook_from_pantry || {};
  const pantry = data.pantry_status || {};
  const expiring = data.expiring_soon || {};
  const shopping = data.shopping_needed || {};
  const savings = data.weekly_savings || {};
  const planner = data.family_planner || {};
  const score = data.family_kitchen_score || {};
  const today = data.today_meals || {};
  const suggestions = data.smart_suggestions || {};
  const firstPantrySuggestion = (cookFromPantry.suggestions || [])[0] || null;
  const firstSuggestion = (suggestions.items || [])[0] || null;
  const shoppingEstimatedTotal = shopping.estimated_total ?? shopping.summary?.estimated_total ?? shopping.summary?.remaining_shopping_total;
  const savingsAmount = savings.estimated_savings ?? savings.savings_amount;
  const savingsStore = savings.best_store || savings.cheapest_store || "";
  const dashboardCurrency = savings.currency || shopping.currency || planner.currency || "EUR";
  const dashboardCurrencyLabel = dashboardCurrency === "USD" ? "USD ($)" : "EUR (€)";
  const plannerHintKey = planner.entitlement_hint?.message_key || "smart_kitchen.planner.hints.start_7_day_plan";
  const plannerLengths = planner.supported_plan_lengths || [7, 14, 30];
  const plannerActiveDays = planner.active_plan_days || 0;
  const plannedMealsCount = planner.planned_meals_count ?? planner.week?.filled_slots ?? 0;
  const plannerCost = planner.estimated_total_cost ?? 0;
  const plannerStorage = planner.default_storage_summary || {};
  const suggestedBatchSizes = planner.suggested_batch_sizes || [];
  const suggestedBatchSizesLabel = suggestedBatchSizes.join(t("smart_kitchen.planner.batch_size_separator"));
  const canUseExtendedPlanning = Boolean(planner.entitlement_hint?.can_use_extended_planning) || ["trial", "premium"].includes(String(data.entitlements?.tier || "").toLowerCase());
  const familyEmptyTitleKey = canUseExtendedPlanning ? "smart_kitchen.empty_states.family_planner.title_extended" : "smart_kitchen.empty_states.family_planner.title_free";
  const prefix = `/${country || "en"}`;
  const routes = {
    mealPlan: `${prefix}/client/meal/plan`,
    inventory: `${prefix}/client/inventory/check`,
    shopping: `${prefix}/client/select-shopping-list`,
    savings: `${prefix}/client/price-intelligence`,
    upgrade: `${prefix}/plans?intent=premium`,
    recipeSearch: `${prefix}/search-recipes?source=smart-kitchen${
      firstSuggestion?.query || firstSuggestion?.title || firstSuggestion?.recipe_name
        ? `&q=${encodeURIComponent(firstSuggestion.query || firstSuggestion.title || firstSuggestion.recipe_name)}`
        : ""
    }`,
    cookFromPantrySearch: `${prefix}/search-recipes?source=smart-kitchen${
      firstPantrySuggestion?.recipe_name
        ? `&q=${encodeURIComponent(firstPantrySuggestion.recipe_name)}`
        : ""
    }`,
    cookFromPantry: firstPantrySuggestion?.recipe_id
      ? `${prefix}/recipe/${firstPantrySuggestion.recipe_id}/${slugForRecipe(firstPantrySuggestion)}?source=smart-kitchen`
      : null,
    suggestion: firstSuggestion?.recipe_id
      ? `${prefix}/recipe/${firstSuggestion.recipe_id}/${slugForRecipe(firstSuggestion)}?source=smart-kitchen`
      : null,
  };
  const scoreTargetRoutes = {
    pantry: routes.inventory,
    meal_plan: routes.mealPlan,
    shopping: routes.shopping,
    savings: routes.savings,
  };
  const scoreNext = score.next_recommendation || (score.recommendations || [])[0] || null;
  const scoreTarget = scoreNext?.target ? scoreTargetRoutes[scoreNext.target] : null;

  return (
    <Container sx={{ mt: { xs: 2, md: 4 }, mb: 6 }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            {t("smart_kitchen.title")}
          </Typography>
          {data.entitlements?.tier ? <Chip size="small" label={t(`smart_kitchen.tier.${data.entitlements.tier}`)} /> : null}
        </Stack>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
          {t("smart_kitchen.subtitle")}
        </Typography>
      </Box>

      {loading ? (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={22} />
              <Typography>{t("smart_kitchen.loading")}</Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      {!loading && !error ? (
        <>
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {t("smart_kitchen.trust_note_title", { currency: dashboardCurrencyLabel })}
          </Typography>
          <Typography variant="body2">
            {t("smart_kitchen.trust_note_body")}
          </Typography>
        </Alert>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SectionCard
              icon={<FactCheckRoundedIcon color="primary" />}
              title={t("smart_kitchen.sections.family_kitchen_score")}
              subtitle={t("smart_kitchen.score.subtitle")}
              state={score.state}
              onClick={null}
              testId="smart-kitchen-action-family-kitchen-score"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.score.empty_title")}
                  body={t("smart_kitchen.score.empty_body")}
                  primaryAction={scoreTarget ? {
                    label: recommendationLabel(scoreNext, t) || t("smart_kitchen.score.default_action"),
                    onClick: () => navigate(scoreTarget),
                    testId: "smart-kitchen-score-next-action",
                  } : null}
                />
              )}
            >
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                  <Typography variant="h3" sx={{ fontWeight: 900 }}>
                    {t("smart_kitchen.score.score_value", { score: score.score || 0, max: score.max_score || 100 })}
                  </Typography>
                  <Chip
                    color={(score.grade || "starter") === "excellent" ? "success" : (score.grade || "starter") === "good" ? "primary" : "warning"}
                    label={t(`smart_kitchen.score.grade.${score.grade || "starter"}`)}
                  />
                </Stack>
                {scoreNext ? (
                  <Alert severity="info" action={scoreTarget ? (
                    <Button size="small" onClick={() => navigate(scoreTarget)}>
                      {t("smart_kitchen.score.open_next")}
                    </Button>
                  ) : null}>
                    {recommendationLabel(scoreNext, t)}
                  </Alert>
                ) : null}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                      {t("smart_kitchen.score.completed")}
                    </Typography>
                    {(score.completed || []).length ? (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {(score.completed || []).map((item) => (
                          <Chip key={item} size="small" color="success" label={scoreItemLabel(item, t)} />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{t("smart_kitchen.score.none_completed")}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                      {t("smart_kitchen.score.missing")}
                    </Typography>
                    {(score.missing || []).length ? (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {(score.missing || []).map((item) => (
                          <Chip key={item} size="small" variant="outlined" label={scoreItemLabel(item, t)} />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{t("smart_kitchen.score.nothing_missing")}</Typography>
                    )}
                  </Grid>
                </Grid>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<Inventory2RoundedIcon color="success" />}
              title={t("smart_kitchen.sections.pantry_health")}
              subtitle={t("smart_kitchen.pantry_health.subtitle")}
              state={pantryHealth.state}
              onClick={null}
              testId="smart-kitchen-action-pantry-health"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.pantry_health.empty_title")}
                  body={t("smart_kitchen.pantry_health.empty_body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.pantry_status.primary"),
                    onClick: () => navigate(routes.inventory),
                    testId: "smart-kitchen-empty-pantry-health-primary",
                  }}
                />
              )}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    {t("smart_kitchen.pantry_health.score_value", {
                      score: pantryHealth.score || 0,
                      max: pantryHealth.max_score || 100,
                    })}
                  </Typography>
                  <Chip
                    color={(pantryHealth.grade || "high_risk") === "excellent" ? "success" : (pantryHealth.grade || "high_risk") === "good" ? "primary" : "warning"}
                    label={pantryHealthGradeLabel(pantryHealth.grade, t)}
                  />
                </Stack>
                {(pantryHealth.reasons || []).length ? (
                  <Stack spacing={0.75}>
                    {(pantryHealth.reasons || []).slice(0, 3).map((reason) => (
                      <Typography key={reason.key} variant="body2" color="text.secondary">
                        {pantryHealthReasonLabel(reason, t)}
                      </Typography>
                    ))}
                  </Stack>
                ) : null}
                {(pantryHealth.recommendations || []).length ? (
                  <Alert severity="info">
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      {t("smart_kitchen.pantry_health.recommendation_label")}
                    </Typography>
                    <Typography variant="body2">
                      {pantryHealthRecommendationLabel((pantryHealth.recommendations || [])[0], t)}
                    </Typography>
                  </Alert>
                ) : null}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<EventBusyRoundedIcon color="warning" />}
              title={t("smart_kitchen.sections.food_waste_risk")}
              subtitle={t("smart_kitchen.food_waste_risk.subtitle")}
              state={foodWasteRisk.state}
              onClick={foodWasteRisk.state === "empty" ? null : () => navigate(routes.inventory)}
              testId="smart-kitchen-action-food-waste-risk"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.food_waste_risk.empty_title")}
                  body={t("smart_kitchen.food_waste_risk.empty_body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.pantry_status.primary"),
                    onClick: () => navigate(routes.inventory),
                    testId: "smart-kitchen-empty-food-waste-risk-primary",
                  }}
                />
              )}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    {t("smart_kitchen.food_waste_risk.score_value", {
                      score: foodWasteRisk.score || 0,
                      max: foodWasteRisk.max_score || 100,
                    })}
                  </Typography>
                  <Chip
                    color={(foodWasteRisk.level || "low") === "low" ? "success" : (foodWasteRisk.level || "low") === "medium" ? "warning" : "error"}
                    label={t(`smart_kitchen.food_waste_risk.level.${foodWasteRisk.level || "low"}`)}
                  />
                </Stack>
                {(foodWasteRisk.reasons || []).length ? (
                  <Stack spacing={0.75}>
                    {(foodWasteRisk.reasons || []).slice(0, 3).map((reason) => (
                      <Typography key={reason.key} variant="body2" color="text.secondary">
                        {foodWasteRiskReasonLabel(reason, t)}
                      </Typography>
                    ))}
                  </Stack>
                ) : null}
                {(foodWasteRisk.recommendations || []).length ? (
                  <Alert severity={(foodWasteRisk.level || "low") === "critical" ? "warning" : "info"}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      {t("smart_kitchen.food_waste_risk.recommendation_label")}
                    </Typography>
                    <Typography variant="body2">
                      {foodWasteRiskRecommendationLabel((foodWasteRisk.recommendations || [])[0], t)}
                    </Typography>
                  </Alert>
                ) : null}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<TipsAndUpdatesRoundedIcon color="primary" />}
              title={t("smart_kitchen.sections.cook_from_pantry")}
              subtitle={t("smart_kitchen.cook_from_pantry.subtitle")}
              state={cookFromPantry.state}
              onClick={cookFromPantry.state === "empty" ? null : () => navigate(routes.cookFromPantry || routes.cookFromPantrySearch)}
              testId="smart-kitchen-action-cook-from-pantry"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.cook_from_pantry.empty_title")}
                  body={t("smart_kitchen.cook_from_pantry.empty_body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.pantry_status.primary"),
                    onClick: () => navigate(routes.inventory),
                    testId: "smart-kitchen-empty-cook-from-pantry-primary",
                  }}
                  secondaryAction={{
                    label: t("smart_kitchen.cook_from_pantry.search_recipes"),
                    onClick: () => navigate(routes.cookFromPantrySearch),
                    testId: "smart-kitchen-empty-cook-from-pantry-secondary",
                  }}
                />
              )}
            >
              <Stack spacing={1.5}>
                <CompactList
                  items={(cookFromPantry.suggestions || []).slice(0, 3)}
                  emptyLabel={t("smart_kitchen.empty_section")}
                  primaryText={(item) => item.recipe_name || t("smart_kitchen.fallback.recipe")}
                  secondaryText={(item) => suggestionDetailText(item, t)}
                />
                {firstPantrySuggestion?.uses_expiring_items ? (
                  <Chip
                    size="small"
                    color="warning"
                    sx={{ alignSelf: "flex-start" }}
                    label={t("smart_kitchen.cook_from_pantry.expiring_badge")}
                  />
                ) : null}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<RestaurantRoundedIcon color="primary" />}
              title={t("smart_kitchen.sections.today_meals")}
              subtitle={today.date || ""}
              state={today.state}
              onClick={today.state === "empty" ? null : () => navigate(routes.mealPlan)}
              testId="smart-kitchen-action-today-meals"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.empty_states.today_meals.title")}
                  body={t("smart_kitchen.empty_states.today_meals.body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.today_meals.primary"),
                    onClick: () => navigate(routes.mealPlan),
                    testId: "smart-kitchen-empty-today-meals-primary",
                  }}
                />
              )}
            >
              <CompactList
                items={today.items || []}
                emptyLabel={t("smart_kitchen.empty_section")}
                primaryText={(item) => item.recipe_name || t("smart_kitchen.fallback.recipe")}
                secondaryText={(item) => [item.meal_type, item.cost ? money(item.cost) : null].filter(Boolean).join(" · ")}
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<Inventory2RoundedIcon color="success" />}
              title={t("smart_kitchen.sections.pantry_status")}
              subtitle={t("smart_kitchen.pantry.total", { count: pantry.total_items || 0 })}
              state={pantry.state}
              onClick={pantry.state === "empty" ? null : () => navigate(routes.inventory)}
              testId="smart-kitchen-action-pantry-status"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.empty_states.pantry_status.title")}
                  body={t("smart_kitchen.empty_states.pantry_status.body")}
                  examples={[
                    t("smart_kitchen.empty_states.pantry_status.examples.milk"),
                    t("smart_kitchen.empty_states.pantry_status.examples.eggs"),
                    t("smart_kitchen.empty_states.pantry_status.examples.bread"),
                  ]}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.pantry_status.primary"),
                    onClick: () => navigate(routes.inventory),
                    testId: "smart-kitchen-empty-pantry-primary",
                  }}
                />
              )}
            >
              <Stack spacing={1}>
                <Typography variant="body2">{t("smart_kitchen.pantry.fridge", { count: pantry.locations?.fridge || 0 })}</Typography>
                <Typography variant="body2">{t("smart_kitchen.pantry.freezer", { count: pantry.locations?.freezer || 0 })}</Typography>
                <Typography variant="body2">{t("smart_kitchen.pantry.pantry", { count: pantry.locations?.pantry || 0 })}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("smart_kitchen.pantry.attention", {
                    expired: pantry.attention?.expired_count || 0,
                    expiring: pantry.attention?.expiring_soon_count || 0,
                  })}
                </Typography>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<EventBusyRoundedIcon color="warning" />}
              title={t("smart_kitchen.sections.expiring_soon")}
              subtitle={t("smart_kitchen.expiring.window", { days: expiring.window_days || 7 })}
              state={expiring.state}
              onClick={expiring.state === "empty" ? null : () => navigate(routes.inventory)}
              testId="smart-kitchen-action-expiring-soon"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.empty_states.expiring_soon.title")}
                  body={t("smart_kitchen.empty_states.expiring_soon.body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.expiring_soon.primary"),
                    onClick: () => navigate(routes.inventory),
                    testId: "smart-kitchen-empty-expiring-primary",
                  }}
                />
              )}
            >
              <CompactList
                items={expiring.items || []}
                emptyLabel={t("smart_kitchen.empty_section")}
                primaryText={(item) => item.name || t("smart_kitchen.fallback.product")}
                secondaryText={(item) => t("smart_kitchen.expiring.item_meta", {
                  status: expiryLabel(item, t),
                  location: item.storage_location || t("smart_kitchen.fallback.location"),
                })}
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<ShoppingCartRoundedIcon color="error" />}
              title={t("smart_kitchen.sections.shopping_needed")}
              subtitle={shopping.active_list?.name || ""}
              state={shopping.state}
              onClick={shopping.state === "empty" ? null : () => navigate(routes.shopping)}
              testId="smart-kitchen-action-shopping-needed"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.empty_states.shopping_needed.title")}
                  body={t("smart_kitchen.empty_states.shopping_needed.body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.shopping_needed.primary"),
                    onClick: () => navigate(routes.mealPlan),
                    testId: "smart-kitchen-empty-shopping-primary",
                  }}
                  secondaryAction={{
                    label: t("smart_kitchen.empty_states.shopping_needed.secondary"),
                    onClick: () => navigate(routes.shopping),
                    testId: "smart-kitchen-empty-shopping-secondary",
                  }}
                />
              )}
            >
              <Stack spacing={1}>
                <Typography variant="body2">
                  {t("smart_kitchen.shopping.summary", {
                    remaining: shopping.remaining_items ?? shopping.summary?.remaining_items ?? 0,
                    total: shopping.total_items ?? shopping.summary?.total_items ?? shopping.summary?.items_total ?? 0,
                  })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("smart_kitchen.shopping.estimated_total", {
                    value: money(shoppingEstimatedTotal, shopping.currency || shopping.summary?.currency || "EUR"),
                  })}
                </Typography>
                <CompactList
                  items={shopping.top_items || shopping.items || []}
                  emptyLabel={t("smart_kitchen.empty_section")}
                  primaryText={(item) => item.name || t("smart_kitchen.fallback.product")}
                  secondaryText={(item) => t("smart_kitchen.shopping.item_meta", {
                    qty: item.remaining_qty || 0,
                    unit: item.unit || "",
                  })}
                />
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<SavingsRoundedIcon color="success" />}
              title={t("smart_kitchen.sections.weekly_savings")}
              subtitle={savings.state === "locked" ? t("smart_kitchen.locked_short") : ""}
              state={savings.state}
              onClick={savings.state === "empty" || savings.state === "locked" ? null : () => navigate(routes.savings)}
              testId="smart-kitchen-action-weekly-savings"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.empty_states.weekly_savings_empty.title")}
                  body={t("smart_kitchen.empty_states.weekly_savings_empty.body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.weekly_savings_empty.primary"),
                    onClick: () => navigate(routes.shopping),
                    testId: "smart-kitchen-empty-savings-primary",
                  }}
                  secondaryAction={{
                    label: t("smart_kitchen.empty_states.weekly_savings_empty.secondary"),
                    onClick: () => navigate(routes.inventory),
                    testId: "smart-kitchen-empty-savings-secondary",
                  }}
                />
              )}
              lockedContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.empty_states.weekly_savings_locked.title")}
                  body={t("smart_kitchen.empty_states.weekly_savings_locked.body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.weekly_savings_locked.primary"),
                    onClick: () => navigate(routes.upgrade),
                    testId: "smart-kitchen-locked-savings-primary",
                  }}
                />
              )}
            >
              <Stack spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {money(savingsAmount, savings.currency || "EUR")}
                </Typography>
                {savingsStore ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("smart_kitchen.savings.best_store", { store: savingsStore })}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  {t("smart_kitchen.savings.summary", {
                    count: savings.opportunity_count || 0,
                    percent: Number(savings.savings_pct || 0).toFixed(1),
                  })}
                </Typography>
                {Number(savings.monthly_savings_amount || 0) > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {(t("monthly_savings") || "Monthly savings")}: {money(savings.monthly_savings_amount, savings.currency || "EUR")}
                  </Typography>
                ) : null}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    {t("smart_kitchen.savings.top_savings")}
                  </Typography>
                  <CompactList
                    items={savings.explanation_items || []}
                    emptyLabel={t("smart_kitchen.savings.no_explanations")}
                    primaryText={(item) => item.generic_product_name || item.product_name || t("smart_kitchen.fallback.product")}
                    secondaryText={(item) => savingsExplanationText(item, savings.currency || "EUR", t)}
                  />
                </Box>
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <SectionCard
              icon={<TipsAndUpdatesRoundedIcon color="info" />}
              title={t("smart_kitchen.sections.smart_suggestions")}
              subtitle=""
              state={suggestions.state}
              onClick={suggestions.state === "empty" ? null : () => navigate(routes.suggestion || routes.recipeSearch)}
              testId="smart-kitchen-action-smart-suggestions"
              emptyContent={(
                <GuidedEmptyState
                  title={t("smart_kitchen.empty_states.smart_suggestions.title")}
                  body={t("smart_kitchen.empty_states.smart_suggestions.body")}
                  primaryAction={{
                    label: t("smart_kitchen.empty_states.smart_suggestions.primary"),
                    onClick: () => navigate(routes.inventory),
                    testId: "smart-kitchen-empty-suggestions-primary",
                  }}
                  secondaryAction={{
                    label: t("smart_kitchen.empty_states.smart_suggestions.secondary"),
                    onClick: () => navigate(routes.mealPlan),
                    testId: "smart-kitchen-empty-suggestions-secondary",
                  }}
                />
              )}
            >
              <CompactList
                items={suggestions.items || []}
                emptyLabel={t("smart_kitchen.empty_section")}
                primaryText={(item) => item.recipe_name || item.title || t("smart_kitchen.fallback.suggestion")}
                secondaryText={(item) => suggestionDetailText(item, t)}
              />
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <Stack spacing={1.25} alignItems="flex-start">
              <Box sx={{ width: "100%" }}>
                <SectionCard
                  icon={<GroupsRoundedIcon color="secondary" />}
                  title={t("smart_kitchen.sections.family_planner")}
                  subtitle={t("smart_kitchen.planner.positioning", { lengths: plannerLengths.join(", ") })}
                  state={planner.state}
                  onClick={planner.state === "empty" ? null : () => navigate(routes.mealPlan)}
                  testId="smart-kitchen-action-family-planner"
                  emptyContent={(
                    <GuidedEmptyState
                      title={t(familyEmptyTitleKey)}
                      body={t("smart_kitchen.empty_states.family_planner.body")}
                      primaryAction={{
                        label: t("smart_kitchen.empty_states.family_planner.primary"),
                        onClick: () => navigate(routes.mealPlan),
                        testId: "smart-kitchen-empty-family-primary",
                      }}
                    />
                  )}
                >
                  <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t(plannerHintKey)}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.household_size")}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {planner.household_size || planner.profile?.household_members || 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.active_days")}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      {plannerActiveDays ? t("smart_kitchen.planner.days_count", { count: plannerActiveDays }) : t("smart_kitchen.empty_value")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.planned_meals")}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{plannedMealsCount}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.estimated_cost")}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{money(plannerCost, planner.currency || "EUR")}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.shopping_trips")}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{planner.shopping_trips_estimate || 0}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.supported")}</Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{plannerLengths.join(" / ")}</Typography>
                  </Grid>
                </Grid>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                    {t("smart_kitchen.planner.leftovers_title")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.leftover_capable")}</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{planner.leftover_capable_recipes_count || 0}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.fridge_friendly")}</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{planner.fridge_friendly_count || 0}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.freezer_friendly")}</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{planner.freezer_friendly_count || 0}</Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("smart_kitchen.planner.default_storage_summary", {
                      fridge: plannerStorage.fridge || 0,
                      freezer: plannerStorage.freezer || 0,
                    })}
                  </Typography>
                  {planner.solo_cooking_supported ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {t("smart_kitchen.planner.solo_cooking_hint", { sizes: suggestedBatchSizesLabel })}
                    </Typography>
                  ) : null}
                </Box>
                <Divider />
                <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.range")}</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {[planner.week?.start_date, planner.week?.end_date].filter(Boolean).join(" - ") || t("smart_kitchen.empty_value")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.filled_slots")}</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{planner.week?.filled_slots || 0}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">{t("smart_kitchen.planner.open_slots")}</Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{planner.week?.open_slots || 0}</Typography>
                </Grid>
                </Grid>
                  </Stack>
                </SectionCard>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`${prefix}/smart-kitchen/split-meals-preview`)}
                data-testid="smart-kitchen-split-meals-preview-link"
              >
                {t("smart_kitchen.split_meals.link_cta")}
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate(`${prefix}/smart-kitchen/pantry-aware-preview`)}
                data-testid="smart-kitchen-pantry-aware-preview-link"
              >
                {t("smart_kitchen.pantry_aware_preview.link_cta")}
              </Button>
            </Stack>
          </Grid>
        </Grid>
        </>
      ) : null}
    </Container>
  );
}
